import React from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import US_STATES from 'datasets-us-states-abbr-names'
import InputContainer from '../../containers/common/InputContainer'
import { Option } from '../common/Select'
import SelectContainer from '../../containers/common/SelectContainer'
import { toTitleCase } from '../../utils/textFormatters'
import { required, isNumeric, isPostalCode } from '../../utils/validators'
import { COUNTRIES, CA_PROVINCES } from '../../constants'

const COUNTRY_CODES = { US: 'US', CA: 'CA' }

const REGION_FIELDS = {
  [COUNTRY_CODES.US]: {
    regionLabel: 'State',
    regionPlaceholder: 'Select a state',
    regions: Object.keys(US_STATES).map(x => ({
      value: x,
      label: toTitleCase(US_STATES[x]),
    })),
    postalCodeLabel: 'Zip Code',
    postalCodeValidators: [isNumeric],
  },
  [COUNTRY_CODES.CA]: {
    regionLabel: 'Province/Territory',
    regionPlaceholder: 'Select a province or territory',
    regions: Object.keys(CA_PROVINCES).map(x => ({
      value: x,
      label: CA_PROVINCES[x],
    })),
    postalCodeLabel: 'Postal Code',
    postalCodeValidators: [isPostalCode],
  },
}

const REQUIRED = [required]
const REQUIRED_POSTAL_CODE_VALIDATORS = {
  [COUNTRY_CODES.US]: [required, isNumeric],
  [COUNTRY_CODES.CA]: [required, isPostalCode],
}

// sites saved before country was tracked only have a state, so infer the
// country from it (province and US state codes never overlap); used to seed
// the form's initial values
const getCountry = (country, state) => {
  if (country) {
    return country
  }

  return state && CA_PROVINCES[state] ? COUNTRY_CODES.CA : COUNTRY_CODES.US
}

const normalizePostalCode = (value, country) =>
  value && country === COUNTRY_CODES.CA ? value.toUpperCase() : value

const CountryRegionFields = ({
  countryName,
  stateName,
  postalCodeName,
  country,
  state,
  change,
  onCountryChange,
  isRequired,
}) => {
  const currentCountry = getCountry(country, state)
  const {
    regionLabel,
    regionPlaceholder,
    regions,
    postalCodeLabel,
    postalCodeValidators,
  } = REGION_FIELDS[currentCountry] || REGION_FIELDS[COUNTRY_CODES.US]

  return (
    <>
      <Field
        name={countryName}
        component={SelectContainer}
        label="Country"
        className="w-20-ns w-100 mr3-ns"
        placeholder="Select a country"
        validate={isRequired ? REQUIRED : null}
        onChange={(_, nextValue, prevValue) => {
          if (nextValue !== prevValue) {
            change(stateName, null)
            change(postalCodeName, null)
            if (onCountryChange) {
              onCountryChange(nextValue)
            }
          }
        }}
      >
        {Object.keys(COUNTRIES).map(x => (
          <Option value={x} key={x}>
            {COUNTRIES[x]}
          </Option>
        ))}
      </Field>
      <Field
        name={stateName}
        component={SelectContainer}
        label={regionLabel}
        className="w-20-ns w-100 mr3-ns"
        placeholder={regionPlaceholder}
        validate={isRequired ? REQUIRED : null}
        filterable
      >
        {regions.map(x => (
          <Option value={x.value} key={x.value}>
            {x.label}
          </Option>
        ))}
      </Field>
      <Field
        name={postalCodeName}
        type="string"
        component={InputContainer}
        label={postalCodeLabel}
        className="w-20-ns w-100"
        normalize={value => normalizePostalCode(value, currentCountry)}
        validate={
          isRequired
            ? REQUIRED_POSTAL_CODE_VALIDATORS[currentCountry]
            : postalCodeValidators
        }
      />
    </>
  )
}

CountryRegionFields.propTypes = {
  countryName: PropTypes.string.isRequired,
  stateName: PropTypes.string.isRequired,
  postalCodeName: PropTypes.string.isRequired,
  country: PropTypes.string,
  state: PropTypes.string,
  change: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func,
  isRequired: PropTypes.bool,
}

CountryRegionFields.defaultProps = { isRequired: false }

export { getCountry, normalizePostalCode, COUNTRY_CODES }

export default CountryRegionFields
